import { useCallback, useEffect, useRef } from 'react';
import { useQontoWebsocket } from '@repo/shared-kit/websocket/use-websocket';
import { useQontoWebsocketConnection } from '@repo/shared-kit/websocket/use-websocket-connection';
import { useConfig } from '@repo/shared-kit/config/config-provider';

export const CONTRACT_SIGN_COMPLETED = 'contract_sign_completed';
export const CONTRACT_SIGN_VALIDATION_ERROR = 'contract_sign_validation_error';
export const CONTRACT_SIGN_ACTIVITY_NOT_ALLOWED = 'contract_sign_activity_not_allowed';

export interface UseContractSignWebSocketOptions {
  token: string;
  userId: string;
  onSuccess?: () => void;
  onValidationError?: () => void;
  onActivityNotAllowed?: () => void;
  onTimeout?: () => void;
  timeoutEnabled: boolean;
  enabled?: boolean;
}

export function useContractSignWebSocket({
  token,
  userId,
  onSuccess,
  onValidationError,
  onActivityNotAllowed,
  onTimeout,
  timeoutEnabled,
  enabled = true,
}: UseContractSignWebSocketOptions) {
  const timeoutRef = useRef<number | null>(null);

  const { urls } = useConfig();

  useQontoWebsocketConnection({
    websocketBaseURL: urls.websocketBaseURL,
    token: enabled ? token : '',
    userId: enabled ? userId : '',
  });

  const clearTimeoutIfExists = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleSuccess = useCallback(() => {
    clearTimeoutIfExists();
    onSuccess?.();
  }, [onSuccess, clearTimeoutIfExists]);

  const handleValidationError = useCallback(() => {
    clearTimeoutIfExists();
    onValidationError?.();
  }, [onValidationError, clearTimeoutIfExists]);

  const handleActivityNotAllowed = useCallback(() => {
    clearTimeoutIfExists();
    onActivityNotAllowed?.();
  }, [onActivityNotAllowed, clearTimeoutIfExists]);

  useQontoWebsocket(CONTRACT_SIGN_COMPLETED, handleSuccess, { enabled });
  useQontoWebsocket(CONTRACT_SIGN_VALIDATION_ERROR, handleValidationError, { enabled });
  useQontoWebsocket(CONTRACT_SIGN_ACTIVITY_NOT_ALLOWED, handleActivityNotAllowed, { enabled });

  useEffect(() => {
    if (enabled && timeoutEnabled) {
      timeoutRef.current = window.setTimeout(() => {
        onTimeout?.();
      }, 4000);
    }

    return () => {
      clearTimeoutIfExists();
    };
  }, [enabled, timeoutEnabled, onTimeout, clearTimeoutIfExists]);
}
